import React, { useState } from 'react';
import { Image, Linking, Platform, TouchableOpacity, View, type StyleProp, type ViewStyle } from 'react-native';
import { Card, Text, useTheme } from 'react-native-paper';
import { useTranslation } from 'react-i18next';
import { APP_STORE_URL, playStoreUrl } from '@/src/config/appStores';
import { currentMobileWebPlatform, type MobileStorePlatform } from '@/src/utils/mobileWebPlatform';

// Visible badge height. Google requires its badge to be at least as large as the
// others; Apple and Google both ask for clear space of a quarter of the height.
const BADGE_HEIGHT = 48;
const BADGE_CLEAR_SPACE = BADGE_HEIGHT / 4;
// The Google PNGs are cropped to the visible badge (no transparent margin).
const GOOGLE_BADGE_ASPECT: Record<string, number> = { 'en-US': 564 / 168, 'pt-BR': 646 / 192, 'pt-PT': 646 / 192 };
const APPLE_BADGE_ASPECT = 119.66407 / 40;

interface Props {
  /** Referral code handed to Android through the Play install referrer. */
  referralCode?: string | null;
  /** Detected phone OS: its store comes first. `null` shows both equally. */
  primary?: MobileStorePlatform | null;
  title?: string;
  message?: string;
  testID?: string;
}


/**
 * Google Play + App Store buttons (ALL-22/ALL-23). Links come only from
 * `src/config/appStores.ts`; the Android link carries the referral code
 * through the install referrer when one is given.
 */
export function AppStoreLinks({ referralCode, title, message, testID = 'app-store-links' }: Props) {
  const { t, i18n } = useTranslation('register');
  const theme = useTheme();
  const [openError, setOpenError] = useState(false);
  const language = (i18n?.resolvedLanguage || i18n?.language || 'pt-PT').replace('_', '-').toLowerCase();
  const locale = language.startsWith('en') ? 'en-US' : language === 'pt-br' ? 'pt-BR' : 'pt-PT';
  const openStoreUrl = async (url: string) => {
    setOpenError(false);
    try { await Linking.openURL(url); } catch { setOpenError(true); }
  };
  const android = (
    <TouchableOpacity
      key="android"
      accessibilityRole="link"
      accessibilityLabel={t('appStoreAndroid')}
      testID="store-link-android"
      onPress={() => { void openStoreUrl(playStoreUrl(referralCode)); }}
    >
      <Image testID="store-badge-android" source={{ uri: `/app-store-badges/google-${locale}.png` }}
        resizeMode="contain" style={{ height: BADGE_HEIGHT, aspectRatio: GOOGLE_BADGE_ASPECT[locale] }} />
    </TouchableOpacity>
  );
  const ios = (
    <TouchableOpacity
      key="ios"
      accessibilityRole="link"
      accessibilityLabel={t('appStoreIos')}
      testID="store-link-ios"
      onPress={() => { void openStoreUrl(APP_STORE_URL); }}
    >
      <Image testID="store-badge-ios" source={{ uri: `/app-store-badges/apple-${locale}.svg` }}
        resizeMode="contain" style={{ height: BADGE_HEIGHT, aspectRatio: APPLE_BADGE_ASPECT }} />
    </TouchableOpacity>
  );
  return (
    <Card testID={testID} style={{ borderRadius: 16, width: '100%', maxWidth: 480, alignSelf: 'center' }}>
      <Card.Content style={{ gap: 12, paddingVertical: 12 }}>
        <Text variant="titleMedium">{title ?? t('appStoresTitle')}</Text>
        <Text variant="bodyMedium" style={{ color: theme.colors.onSurfaceVariant }}>
          {message ?? t('appStoresMessage')}
        </Text>
        {/* Apple requires its preferred black badge first; never recolor the artwork. */}
        <View testID="store-badges" style={{ flexDirection: 'row', flexWrap: 'wrap', justifyContent: 'center',
          alignItems: 'center', gap: BADGE_CLEAR_SPACE }}>{[ios, android]}</View>
        {openError && <Text accessibilityRole="alert" style={{ color: theme.colors.error }}>{t('appStoreOpenError')}</Text>}
        <Text variant="bodySmall" style={{ color: theme.colors.onSurfaceVariant }}>{t('appStoreTrademarks')}</Text>
      </Card.Content>
    </Card>
  );
}

/**
 * End-of-registration store links (ALL-23). Only the web needs them: inside the
 * native app the person already has it installed.
 */
export function RegistrationAppStoreLinks({ style }: { style?: StyleProp<ViewStyle> }) {
  if (Platform.OS !== 'web') return null;
  return (
    <View style={style}>
      <AppStoreLinks testID="registration-app-store-links" primary={currentMobileWebPlatform()} />
    </View>
  );
}

export default AppStoreLinks;
